import { TriangleAlert } from "lucide-react";
import { Navigate, useParams } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useMandateTemplates,
  usePlatforms,
  usePortfolioCollection,
  usePortfolioCollectionSettings,
  useProducts,
} from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";

import { TableCardSkeleton } from "../skeleton-loaders";
import { MandateRulesCard } from "./mandate-rules-card";
import { MandateTemplatesTable } from "./mandate-templates-table";
import { T } from "./page.i18n";

/**
 * QL-375 — the Mandates tab: the collection's enforcement rules on top, the
 * templates table below it (group 2 of the implementation plan). Reachable
 * only while the collection has mandates enabled (the Settings tab's switch).
 */
export default function CollectionMandatesPage() {
  const { id } = useParams<{ id: string }>();
  const { settings, error, isLoading } = usePortfolioCollectionSettings(id!);
  const { collection } = usePortfolioCollection(id!);
  const { templates, isLoading: templatesLoading } = useMandateTemplates(id!);
  const { platforms } = usePlatforms(id!);
  const { products } = useProducts(id!);
  const t = useT(T);

  if (isLoading || templatesLoading || !settings) {
    if (error) {
      return (
        <Alert variant="destructive">
          <AlertDescription>{t.failedToLoad}</AlertDescription>
        </Alert>
      );
    }
    return <TableCardSkeleton columns={4} />;
  }

  // The tab is hidden while mandates are off — catch direct URLs too (PR 598 review).
  if (collection && !collection.mandatesEnabled) {
    return <Navigate to={`/settings/portfolios/${id}/settings`} replace />;
  }

  const noGoalTypes = collection?.mandateStatus != null && !collection.mandateStatus.hasGoalTypes;

  return (
    <div className="space-y-6">
      {noGoalTypes && (
        <Alert variant="warning">
          <AlertDescription className="flex items-center gap-2">
            <TriangleAlert className="h-4 w-4 shrink-0 text-warning" />
            {t.noGoalTypesWarning}
          </AlertDescription>
        </Alert>
      )}
      <MandateRulesCard portfolioCollectionId={id!} settings={settings} />
      <MandateTemplatesTable
        portfolioCollectionId={id!}
        templates={templates}
        status={collection?.mandateStatus}
        // The matrix has several rows per score (one per horizon); templates
        // are keyed by the distinct scores, ascending.
        riskScores={[
          ...new Set(collection?.riskModel?.matrixScores.map((ms) => ms.score) ?? []),
        ].sort((a, b) => a - b)}
        platforms={platforms}
        products={products}
      />
    </div>
  );
}
